import { ArrowLeft, BookOpen, CheckCircle, Code, Info, Settings, Zap } from 'lucide-react';
import type { HelpNodeContent } from '../data/help-content';

interface HelpArticleProps {
	node: HelpNodeContent;
	onBack: () => void;
	embedded?: boolean;
}

export function HelpArticle({ node, onBack, embedded = false }: HelpArticleProps) {
	const categoryColors = {
		triggers: 'green',
		'flow-control': 'emerald',
		processing: 'blue',
		integrations: 'purple',
		advanced: 'orange',
	};

	const color = categoryColors[node.category];

	// If embedded, render with compact styling
	if (embedded) {
		return (
			<div className="flex flex-col h-full pointer-events-auto">
				{/* Compact Header */}
				<div className="pb-4 border-b border-gray-200 dark:border-gray-700">
					<div className="flex items-center gap-3 mb-3">
						<button
							onClick={onBack}
							className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
						>
							<ArrowLeft className="w-4 h-4" />
						</button>
						<div className="flex items-center gap-2">
							<span className="text-xl">{node.icon}</span>
							<div>
								<h1 className="text-lg font-semibold text-gray-900 dark:text-white">
									{node.name}
								</h1>
								<div
									className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-${color}-100 dark:bg-${color}-900 text-${color}-700 dark:text-${color}-300`}
								>
									{node.category.charAt(0).toUpperCase() +
										node.category.slice(1).replace('-', ' ')}
								</div>
							</div>
						</div>
					</div>
					<p className="text-gray-600 dark:text-gray-300 text-sm">{node.description}</p>
				</div>

				{/* Compact Content */}
				<div className="flex-1 overflow-y-auto py-4">
					<div className="space-y-6">
						{/* When to Use */}
						<section>
							<h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white mb-3">
								<Zap className="w-4 h-4" />
								When to Use
							</h2>
							<ul className="space-y-1.5">
								{node.whenToUse.map((use, index) => (
									<li key={index} className="flex items-start gap-2">
										<CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
										<span className="text-sm text-gray-700 dark:text-gray-300">{use}</span>
									</li>
								))}
							</ul>
						</section>

						{/* Configuration Options */}
						{node.configurationOptions && node.configurationOptions.length > 0 && (
							<section>
								<h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white mb-3">
									<Settings className="w-4 h-4" />
									Configuration Options
								</h2>
								<div className="overflow-x-auto">
									<table className="w-full border-collapse border border-gray-200 dark:border-gray-600 rounded-lg text-sm">
										<thead>
											<tr className="bg-gray-50 dark:bg-gray-700">
												<th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100">
													Field
												</th>
												<th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100">
													Type
												</th>
												<th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100">
													Example
												</th>
												<th className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-left text-xs font-medium text-gray-900 dark:text-gray-100">
													Description
												</th>
											</tr>
										</thead>
										<tbody>
											{node.configurationOptions.map((option, index) => (
												<tr key={index} className="bg-white dark:bg-gray-800">
													<td className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-xs">
														<div className="flex items-center gap-2">
															<span className="font-mono text-gray-900 dark:text-gray-100">
																{option.name}
															</span>
															{option.required && (
																<span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded">
																	Required
																</span>
															)}
														</div>
													</td>
													<td className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-xs">
														<span className="font-mono text-gray-700 dark:text-gray-300">
															{option.type}
														</span>
													</td>
													<td className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-xs">
														<span className="font-mono text-gray-700 dark:text-gray-300">
															{option.example}
														</span>
													</td>
													<td className="border border-gray-200 dark:border-gray-600 px-3 py-2 text-xs">
														<span className="text-gray-700 dark:text-gray-300">
															{option.description}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</section>
						)}

						{/* Example - only show if example exists */}
						{node.example && (
							<section>
								<h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white mb-3">
									<Code className="w-4 h-4" />
									Example
								</h2>
								<div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
									<div className="text-sm font-medium text-gray-900 dark:text-white mb-2">
										{node.example.title}
									</div>
									<div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
										{node.example.description}
									</div>
									{node.example.config && (
										<pre className="text-xs bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600 overflow-x-auto">
											<code className="text-gray-800 dark:text-gray-200">
												{JSON.stringify(node.example.config, null, 2)}
											</code>
										</pre>
									)}
								</div>
							</section>
						)}

						{/* Errors & Failures - only show if errorsFailures exist */}
						{node.errorsFailures && node.errorsFailures.length > 0 && (
							<section>
								<h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white mb-3">
									<Info className="w-4 h-4" />
									Errors & Failures
								</h2>
								<ul className="space-y-1.5">
									{node.errorsFailures.map((error, index) => (
										<li key={index} className="flex items-start gap-2">
											<CheckCircle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
											<span className="text-sm text-gray-700 dark:text-gray-300">{error}</span>
										</li>
									))}
								</ul>
							</section>
						)}
					</div>
				</div>
			</div>
		);
	}

	// Full mode (original implementation)
	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="p-6 border-b border-gray-200 dark:border-gray-700">
				<div className="flex items-center gap-4 mb-4">
					<button
						onClick={onBack}
						className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<div className="flex items-center gap-3">
						<span className="text-2xl">{node.icon}</span>
						<div>
							<h1 className="text-2xl font-bold text-gray-900 dark:text-white">
								{node.name}
							</h1>
							<div
								className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-${color}-100 dark:bg-${color}-900 text-${color}-700 dark:text-${color}-300`}
							>
								{node.category.charAt(0).toUpperCase() +
									node.category.slice(1).replace('-', ' ')}
							</div>
						</div>
					</div>
				</div>
				<p className="text-gray-600 dark:text-gray-300 text-lg">{node.description}</p>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-y-auto p-6">
				<div className="max-w-4xl space-y-8">
					{/* When to Use */}
					<section>
						<h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
							<Zap className="w-5 h-5" />
							When to Use
						</h2>
						<ul className="space-y-2">
							{node.whenToUse.map((use, index) => (
								<li key={index} className="flex items-start gap-2">
									<CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
									<span className="text-gray-700 dark:text-gray-300">{use}</span>
								</li>
							))}
						</ul>
					</section>

					{/* Configuration Options */}
					{node.configurationOptions && node.configurationOptions.length > 0 && (
						<section>
							<h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
								<Settings className="w-5 h-5" />
								Configuration Options
							</h2>
							<div className="overflow-x-auto">
								<table className="w-full border-collapse border border-gray-200 dark:border-gray-600 rounded-lg">
									<thead>
										<tr className="bg-gray-50 dark:bg-gray-700">
											<th className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-medium text-gray-900 dark:text-gray-100">
												Field
											</th>
											<th className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-medium text-gray-900 dark:text-gray-100">
												Type
											</th>
											<th className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-medium text-gray-900 dark:text-gray-100">
												Example
											</th>
											<th className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-medium text-gray-900 dark:text-gray-100">
												Description
											</th>
										</tr>
									</thead>
									<tbody>
										{node.configurationOptions.map((option, index) => (
											<tr key={index} className="bg-white dark:bg-gray-800">
												<td className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-sm">
													<div className="flex items-center gap-2">
														<span className="font-mono text-gray-900 dark:text-gray-100">
															{option.name}
														</span>
														{option.required && (
															<span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded">
																Required
															</span>
														)}
													</div>
												</td>
												<td className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-sm">
													<span className="font-mono text-gray-700 dark:text-gray-300">
														{option.type}
													</span>
												</td>
												<td className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-sm">
													<span className="font-mono text-gray-700 dark:text-gray-300">
														{option.example}
													</span>
												</td>
												<td className="border border-gray-200 dark:border-gray-600 px-4 py-3 text-sm">
													<span className="text-gray-700 dark:text-gray-300">
														{option.description}
													</span>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</section>
					)}

					{/* Example - only show if example exists */}
					{node.example && (
						<section>
							<h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
								<Code className="w-5 h-5" />
								Example
							</h2>
							<div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
								<div className="text-base font-medium text-gray-900 dark:text-white mb-2">
									{node.example.title}
								</div>
								<div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
									{node.example.description}
								</div>
								{node.example.config && (
									<pre className="text-sm bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-600 overflow-x-auto">
										<code className="text-gray-800 dark:text-gray-200">
											{JSON.stringify(node.example.config, null, 2)}
										</code>
									</pre>
								)}
							</div>
						</section>
					)}

					{/* Errors & Failures - only show if errorsFailures exist */}
					{node.errorsFailures && node.errorsFailures.length > 0 && (
						<section>
							<h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
								<Info className="w-5 h-5" />
								Errors & Failures
							</h2>
							<ul className="space-y-2">
								{node.errorsFailures.map((error, index) => (
									<li key={index} className="flex items-start gap-2">
										<CheckCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
										<span className="text-gray-700 dark:text-gray-300">{error}</span>
									</li>
								))}
							</ul>
						</section>
					)}
				</div>
			</div>
		</div>
	);
}
